'use client';
/**
 * @file     VowMarketStatsPanel.tsx
 * @purpose  Registrant-gated sold/off-market aggregate stats for a scope (city/neighbourhood/province), on the hub kit. Gated visitors see GatedPanel (drawn shapes, never values) and the VowAccessModal flow; registrants see the latest market_snapshots vow blob as a StatRow and MeterRows. Numbers NEVER render in public HTML (board posture 2026-07-09).
 */

import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { GatedPanel } from '@/components/gate/GatedPanel';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { MeterRow } from '@/components/hub/MeterRow';
import { Notice } from '@/components/hub/Notice';
import { SectionHeader } from '@/components/hub/SectionHeader';
import { StatRow } from '@/components/hub/StatRow';
import {
  useOptionalVowMarketAccess,
  useVowMarketAccess,
  VowMarketAccessProvider,
} from '@/components/vow/VowMarketAccessProvider';

type Props = {
  scope: 'province' | 'city' | 'neighbourhood';
  scopeKey: string;
  /** Where the verification email should land the user back. */
  redirectPath: string;
  heading?: string;
};

const fmtPrice = (n: number | null) =>
  n == null
    ? null
    : n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`
      : `$${Math.round(n / 1000)}K`;

export function VowMarketStatsPanel({ scope, scopeKey, redirectPath, heading = 'Sold, pending and expired' }: Props) {
  const sharedAccess = useOptionalVowMarketAccess();
  const matchesProvider = sharedAccess?.scope === scope && sharedAccess.scopeKey === scopeKey;
  if (matchesProvider) return <VowMarketStatsPanelBody heading={heading} />;
  return (
    <VowMarketAccessProvider scope={scope} scopeKey={scopeKey} redirectPath={redirectPath}>
      <VowMarketStatsPanelBody heading={heading} />
    </VowMarketAccessProvider>
  );
}

function VowMarketStatsPanelBody({ heading }: { heading: string }) {
  const { t } = useTranslation();
  const { loading, locked, status, gate, vow, capturedAt, openUnlock, reload } = useVowMarketAccess();

  const header = <SectionHeader title={heading} as="h3" size="compact" />;

  if (locked) {
    return (
      <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {header}
        <GatedPanel
          title={t('vowMarketStats.gate.title', { defaultValue: 'Sold and expired numbers need a free account' })}
          description={t('vowMarketStats.gate.description', { defaultValue: 'See these numbers with a free, verified account.' })}
          ctaLabel={
            gate?.isAuthenticated
              ? gate.needsReactivation
                ? t('vowMarketStats.gate.reactivate', { defaultValue: 'Reactivate access' })
                : t('vowMarketStats.gate.verify', { defaultValue: 'Verify your account' })
              : t('vowMarketStats.gate.create', { defaultValue: 'Create a free account' })
          }
          onUnlock={openUnlock}
          rows={3}
        />
      </Box>
    );
  }

  if (status === 'empty' || status === 'error') {
    return (
      <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {header}
        {status === 'empty' ? (
          <Notice>No sold or expired numbers for this area yet.</Notice>
        ) : (
          <Notice tone="error" action={<HubActionButton variant="secondary" action={{ label: 'Try again', onClick: reload }} />}>
            These numbers did not load.
          </Notice>
        )}
      </Box>
    );
  }

  const maxStatus = vow ? Math.max(vow.closedLast90d, vow.pendingCount, vow.expiredOrTerminatedLast90d, 1) : 1;
  const statusRows = vow
    ? [
        { label: 'Sold, 90 days', value: vow.closedLast90d },
        { label: 'Pending', value: vow.pendingCount },
        { label: 'Expired or terminated, 90 days', value: vow.expiredOrTerminatedLast90d },
      ]
    : [];

  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {header}
      <StatRow
        loading={loading}
        ariaLabel={heading}
        updatedAt={loading ? null : capturedAt}
        items={[
          { label: 'Sold in the last 90 days', value: vow ? vow.closedLast90d : null },
          { label: 'Median sold price', value: vow ? fmtPrice(vow.medianClosePrice) : null },
          { label: 'Sale-to-list ratio', value: vow?.saleToListRatio != null ? `${Math.round(vow.saleToListRatio * 100)}%` : null },
          { label: 'Median days to off-market', value: vow?.medianDaysToOffMarket ?? null },
        ]}
      />
      {statusRows.length > 0 ? (
        <Box role="group" aria-label="How properties left the active market">
          {statusRows.map((row) => (
            <MeterRow
              key={row.label}
              label={row.label}
              value={row.value}
              max={maxStatus}
              valueText={row.value.toLocaleString('en-CA')}
              tone="primary"
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
